import { useEffect, useState } from "react";
import api, { formatMoney, API_BASE } from "@/lib/api";
import { PageHeader, Panel, Pill } from "@/components/Bits";
import { toast } from "sonner";
import { CurrencyCircleDollar, ShieldCheck, Download } from "@phosphor-icons/react";
export default function ParentFees() {
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [credits, setCredits] = useState({ credits: [], unredeemed_total: 0, redeemed_total: 0 });
  const [paying, setPaying] = useState(null);

  const load = async () => {
    // Fetch credits FIRST so we can detect "just redeemed" state
    const c = await api.get("/referrals/credits").catch(() => ({ data: { credits: [], unredeemed_total: 0, redeemed_total: 0 } }));
    setCredits(c.data);
    // THEN fetch invoices (which auto-applies credits on the server)
    const [i, p, c2] = await Promise.all([
      api.get("/invoices"),
      api.get("/payments"),
      api.get("/referrals/credits").catch(() => ({ data: { credits: [], unredeemed_total: 0, redeemed_total: 0 } })),
    ]);
    setInvoices(i.data);
    setPayments(p.data);
    // Detect "just redeemed" delta: credit was unredeemed before, now redeemed
    const justRedeemed = c.data.unredeemed_total - c2.data.unredeemed_total;
    if (justRedeemed > 0) {
      toast.success(`🎉 ₹${justRedeemed.toLocaleString("en-IN")} referral credit just applied to your invoice!`, { duration: 6000 });
    }
    setCredits(c2.data);
  };
  useEffect(() => { load(); }, []);

  // Handle Stripe redirect-back: poll /api/payments/status/{session_id}
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const cancelled = params.get("cancelled");
    if (cancelled) {
      toast.info("Payment cancelled. You can try again whenever you're ready.");
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }
    if (!sessionId) return;
    let attempts = 0;
    const max = 8;
    toast.loading("Verifying your payment…", { id: "pay-poll" });
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.status === "paid") {
          toast.success("Payment received — receipt is being generated!", { id: "pay-poll" });
          window.history.replaceState({}, "", window.location.pathname);
          load();
          return;
        }
        if (data.status === "expired" || data.status === "failed") {
          toast.error("Payment did not complete. Please try again.", { id: "pay-poll" });
          window.history.replaceState({}, "", window.location.pathname);
          return;
        }
        if (attempts >= max) {
          toast.message("Still processing — we'll email you when it lands.", { id: "pay-poll" });
          window.history.replaceState({}, "", window.location.pathname);
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (attempts >= max) {
          toast.error("Could not confirm payment. Refresh the page in a moment.", { id: "pay-poll" });
          return;
        }
        setTimeout(poll, 2500);
      }
    };
    poll();
  }, []);

  const due = invoices.reduce((s, i) => s + (i.amount_due || 0), 0);
  const paid = invoices.reduce((s, i) => s + (i.amount_paid || 0), 0);

  return (
    <div>
      <PageHeader eyebrow="Fees" title="Pay & receipts" subtitle="Pay tuition, see receipts, breathe easy." />

      {credits.unredeemed_total > 0 && (
        <div className="mb-5 p-4 rounded-3xl bg-gradient-to-r from-mustard-soft to-sage-soft border border-sage-deep/30 flex items-center gap-3" data-testid="referral-credit-banner">
          <span className="text-3xl">🎉</span>
          <div className="flex-1">
            <div className="font-heading font-bold">You have {formatMoney(credits.unredeemed_total)} in referral credit</div>
            <div className="text-xs text-ink-soft mt-0.5">It will automatically apply to your next quarterly invoice.</div>
          </div>
        </div>
      )}
      {credits.unredeemed_total === 0 && credits.redeemed_total > 0 && (
        <div className="mb-5 p-3 rounded-2xl bg-sage-soft/50 border border-sage-deep/20 flex items-center gap-3" data-testid="referral-credit-history">
          <span className="text-xl">✓</span>
          <div className="text-xs text-ink-soft">
            <span className="font-semibold text-ink">{formatMoney(credits.redeemed_total)}</span> in referral credits already applied to your fees. Thank you for sharing MangoCubs 💛
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <Panel className="lg:col-span-2 bg-gradient-to-br from-terracotta to-terracotta-hover text-white border-0 relative overflow-hidden">
          <div className="absolute -bottom-10 -right-10 w-48 h-48 rounded-full bg-mustard/40 blur-3xl" />
          <div className="text-xs uppercase tracking-wider opacity-80">Outstanding</div>
          <div className="font-heading text-5xl font-bold mt-1">{formatMoney(due)}</div>
          <div className="opacity-80 mt-2 text-sm">Paid so far: {formatMoney(paid)}</div>
          {due > 0 && (
            <button onClick={() => setPaying(invoices.find(i => i.amount_due > 0))} className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-terracotta font-semibold" data-testid="pay-now-btn">
              <CurrencyCircleDollar weight="duotone" /> Pay now
            </button>
          )}
        </Panel>

        <Panel>
          <div className="flex items-center gap-2 text-sage-deep"><ShieldCheck weight="duotone" /><span className="text-sm font-semibold">Secure & instant</span></div>
          <div className="mt-3 text-sm text-ink-soft">Payments are securely processed and receipts are sent within seconds. GST-ready invoices on request.</div>
        </Panel>
      </div>

      <Panel title="Your invoices" className="mb-6">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-ink-soft"><th className="py-2">Period</th><th>Total</th><th>Paid</th><th>Due</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.invoice_id} className="border-t border-border">
                  <td className="py-3 font-medium">{inv.period}</td>
                  <td className="py-3">{formatMoney(inv.amount_total, inv.currency)}</td>
                  <td className="py-3 text-sage-deep">{formatMoney(inv.amount_paid, inv.currency)}</td>
                  <td className="py-3 text-terracotta">{formatMoney(inv.amount_due, inv.currency)}</td>
                  <td className="py-3"><Pill tone={inv.status === "paid" ? "sage" : inv.status === "partial" ? "mustard" : "terracotta"}>{inv.status}</Pill></td>
                  <td className="py-3">{inv.amount_due > 0 && <button onClick={() => setPaying(inv)} className="text-xs px-3 py-1.5 rounded-full bg-terracotta text-white" data-testid={`p-pay-${inv.invoice_id}`}>Pay</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title="Payment history">
        <div className="space-y-2">
          {payments.map(p => (
            <div key={p.payment_id} className="flex items-center justify-between p-3 rounded-2xl bg-paper-alt">
              <div>
                <div className="font-semibold">{formatMoney(p.amount, p.currency)}</div>
                <div className="text-xs text-ink-soft">{new Date(p.paid_at).toLocaleString()} · {p.mode}</div>
              </div>
              <div className="flex items-center gap-2">
                <Pill tone="sage">Receipt #{p.receipt_no}</Pill>
                <ReceiptButton paymentId={p.payment_id} />
              </div>
            </div>
          ))}
        </div>
      </Panel>

      {paying && <ParentPayModal inv={paying} onClose={() => setPaying(null)} onDone={() => { setPaying(null); load(); }} />}
    </div>
  );
}

function ReceiptButton({ paymentId }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("factskoot_token");
      const resp = await fetch(`${API_BASE}/payments/${paymentId}/receipt.pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!resp.ok) throw new Error("Failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `receipt-${paymentId.slice(-6)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download receipt"); }
    finally { setBusy(false); }
  };
  return (
    <button onClick={download} disabled={busy}
      className="inline-flex items-center gap-1 text-xs font-semibold text-terracotta hover:underline disabled:opacity-50"
      data-testid={`receipt-dl-${paymentId}`}>
      <Download weight="duotone" /> Receipt
    </button>
  );
}

function ParentPayModal({ inv, onClose, onDone }) {
  const [amount, setAmount] = useState(inv?.amount_due || 0);
  const [loading, setLoading] = useState(false);

  // Wait briefly for Razorpay JS SDK if it's still loading (in head with async).
  const waitForRazorpay = () => new Promise((resolve) => {
    if (typeof window !== "undefined" && window.Razorpay) return resolve(true);
    let tries = 0;
    const tick = () => {
      tries += 1;
      if (typeof window !== "undefined" && window.Razorpay) return resolve(true);
      if (tries > 30) return resolve(false);
      setTimeout(tick, 100);
    };
    tick();
  });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data: checkout } = await api.post("/payments/checkout", {
        invoice_id: inv.invoice_id,
        origin_url: window.location.origin,
      });

      if (checkout.provider === "stripe") {
        // Stripe redirect flow — server has already created a payment_transactions row.
        toast.message("Redirecting to secure checkout…");
        window.location.href = checkout.url;
        return;
      }

      // Razorpay Standard Checkout — opens the modal client-side.
      const ok = await waitForRazorpay();
      if (!ok || !window.Razorpay) {
        toast.error("Payment widget couldn't load. Please refresh and try again.");
        setLoading(false);
        return;
      }
      const me = JSON.parse(localStorage.getItem("factskoot_user") || "{}");
      const rzp = new window.Razorpay({
        key: checkout.key_id,
        amount: checkout.amount,
        currency: checkout.currency,
        order_id: checkout.order_id,
        name: "MangoCubs",
        description: `Tuition fee · ${inv.period || "Invoice"}`,
        prefill: {
          name: me.name || me.full_name || "",
          email: me.email || "",
          contact: me.phone || me.contact || "",
        },
        notes: { invoice_id: inv.invoice_id },
        theme: { color: "#F58A1F" },
        handler: async (resp) => {
          try {
            await api.post("/payments/razorpay/verify", {
              invoice_id: inv.invoice_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              amount: Number(amount),
            });
            toast.success("Payment successful — receipt on the way!");
            onDone();
          } catch {
            toast.error("Payment captured but verification failed. Contact support.");
          } finally {
            setLoading(false);
          }
        },
        modal: {
          ondismiss: () => {
            toast.info("Payment cancelled.");
            setLoading(false);
          },
        },
      });
      rzp.on("payment.failed", (e2) => {
        toast.error(e2?.error?.description || "Payment failed. Please retry.");
        setLoading(false);
      });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose}>
      <form onSubmit={submit} onClick={e=>e.stopPropagation()} className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl border border-border p-6 space-y-4" data-testid="parent-pay-modal">
        <h3 className="font-heading text-xl font-bold">Pay fees securely</h3>
        <div className="text-sm text-ink-soft">Outstanding: <span className="text-terracotta font-semibold">{formatMoney(inv.amount_due, inv.currency)}</span></div>
        <input type="number" min={1} max={inv.amount_due} value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full h-11 px-3 rounded-xl border border-border" data-testid="parent-pay-amount" />
        <div className="rounded-2xl bg-sage-soft p-3 text-xs text-sage-deep flex items-start gap-2">
          <ShieldCheck weight="duotone" />
          <div>
            {inv.currency === "INR"
              ? "Bank-grade encryption · UPI / Cards / Netbanking / Wallets · Powered by Razorpay."
              : "Bank-grade encryption · Cards / Wallets · Powered by Stripe Checkout."}
          </div>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 h-11 rounded-full border border-border" data-testid="parent-pay-cancel">Cancel</button>
          <button disabled={loading} className="flex-1 h-11 rounded-full bg-terracotta text-white font-semibold" data-testid="parent-pay-submit">{loading ? "Processing…" : `Pay ${formatMoney(amount, inv.currency)}`}</button>
        </div>
      </form>
    </div>
  );
}
